/**
 * Şeritteki tek bir sekme.
 *
 * Sabitlenmiş sekme Chrome'daki gibi davranır: daralır, yalnızca ikon kalır ve kapat
 * düğmesi görünmez — sabitlemenin amacı sekmenin yanlışlıkla kapanmaması.
 */
import { memo, type PointerEvent as ReactPointerEvent } from 'react';
import { Pin, SplitSquareHorizontal, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { NavIcon } from '../../navMenu/NavIcon';
import { tabLabel, type TabModel } from '../model/types';
import { InlineRename } from './InlineRename';

export interface TabItemProps {
  tab: TabModel;
  active: boolean;
  /** Yan yana görünümde sağ panelde duran sekme. */
  inSplit: boolean;
  renaming: boolean;
  dragging: boolean;
  onActivate: () => void;
  onClose: () => void;
  onRenameCommit: (value: string) => void;
  onRenameCancel: () => void;
  onRenameStart: () => void;
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
}

export const TabItem = memo(function TabItem({
  tab,
  active,
  inSplit,
  renaming,
  dragging,
  onActivate,
  onClose,
  onRenameCommit,
  onRenameCancel,
  onRenameStart,
  onPointerDown,
}: TabItemProps) {
  const { t } = useTranslation();
  const label = tabLabel(tab);

  return (
    <div
      role="tab"
      tabIndex={0}
      data-tab-id={tab.tabId}
      aria-selected={active}
      title={label}
      onPointerDown={onPointerDown}
      onClick={() => !renaming && onActivate()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!tab.pinned) onRenameStart();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onActivate();
        if (e.key === 'F2' && !tab.pinned) onRenameStart();
      }}
      onAuxClick={(e) => e.button === 1 && onClose()}
      className={cn(
        'group relative flex h-8 shrink-0 cursor-default items-center gap-1.5 rounded-t-md text-sm',
        tab.pinned ? 'w-10 justify-center px-0' : 'w-[11rem] pr-1 pl-3',
        active ? 'bg-bg-elevated text-fg' : 'text-fg-muted hover:bg-bg-hover',
        dragging && 'opacity-60',
      )}
    >
      <NavIcon
        node={{ iconId: tab.iconId ?? null, screenTypeName: tab.screenTypeName ?? null, children: [] }}
        className="h-3.5 w-3.5 shrink-0"
      />
      {tab.pinned ? (
        // Sabit sekmede ad yok; hangi ekran olduğu ikon + tooltip ile anlaşılır.
        <Pin className="text-fg-subtle absolute right-0.5 bottom-0.5 h-2.5 w-2.5" />
      ) : renaming ? (
        <InlineRename
          value={label}
          aria-label={t('shell.tab.rename')}
          onCommit={onRenameCommit}
          onCancel={onRenameCancel}
        />
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {inSplit ? <SplitSquareHorizontal className="text-brand-600 h-3 w-3 shrink-0" /> : null}
          <button
            type="button"
            aria-label={t('shell.tab.close')}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-fg-subtle hover:bg-bg-hover hover:text-fg shrink-0 rounded p-0.5 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 aria-selected:opacity-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </div>
  );
});
